import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  useUpdateUserMutation,
  useUpdateUserImageMutation,
} from "../../../../store/auth/usersApiSlice";
import { setUserInfo } from "../../../../store/auth/authSlice";
import { uploadFilePath } from "../../../../constants/s3UploadPath";
import { uploadFileToS3 } from "../../../../utils/uploadFileToS3";
import { CommonToast } from "../../../common/Toast";
import { initialProfileValues, profilePayload, validateProfile } from "./profileFields";

/* The profile form, wired. TRANSCRIBED from UserProfileForm.jsx: the same two mutations, the same validation order, the same S3 upload path and category, the... */
export const useUserProfile = () => {
  const dispatch = useDispatch();
  const { userInfo, token } = useSelector((state) => state.auth);

  const [isEditMode, setIsEditMode] = useState(false);
  const [values, setValues] = useState(() => initialProfileValues(userInfo));
  const [error, setError] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);

  const [updateUser, { isLoading: saving }] = useUpdateUserMutation();
  const [updateUserImage] = useUpdateUserImageMutation();

  const setField = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    /* Clearing on edit rather than only on the next submit: leaving "Name
       can't be blank." under a field the viewer has just filled in is what the
       live form does, and it reads as the form not noticing. */
    setError((prev) => (prev?.field === field ? null : prev));
  };

  const startEdit = () => setIsEditMode(true);

  /* Transcribed: cancelling restores the saved values and leaves edit mode. */
  const cancelEdit = () => {
    setValues(initialProfileValues(userInfo));
    setError(null);
    setIsEditMode(false);
  };

  const save = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    const problem = validateProfile(values);
    if (problem) {
      setError(problem);
      return false;
    }

    try {
      const res = await updateUser({ data: profilePayload(values), token }).unwrap();
      dispatch(setUserInfo({ ...res }));
      setIsEditMode(false);
      setError(null);
      return true;
    } catch (err) {
      /* The live handler does not catch at all, so a rejected save became an
         unhandled rejection and the form sat there looking saved. */
      CommonToast.error(
        err?.data?.detail || err?.data?.message || "Couldn't save your profile. Please try again."
      );
      return false;
    }
  };

  /* Transcribed: straight to S3, then the returned URL is written to the user
     through a FormData with the single key. */
  const uploadImage = async (file) => {
    if (!file) return;
    setImageBusy(true);
    try {
      const uploaded = await uploadFileToS3(
        uploadFilePath?.userProfile,
        file,
        token,
        "",
        "user_profile_image"
      );
      if (uploaded?.success === false) {
        /* uploadFileToS3 reports its own reason via a toast. */
        return;
      }
      const formData = new FormData();
      formData.append("profile_image", uploaded?.url);
      const res = await updateUserImage({ data: formData, token }).unwrap();
      dispatch(setUserInfo({ ...res }));
    } catch (err) {
      CommonToast.error("Couldn't update your photo. Please try again.");
    } finally {
      setImageBusy(false);
    }
  };

  /* Transcribed: an empty value is what clears it. */
  const removeImage = async () => {
    setImageBusy(true);
    try {
      const formData = new FormData();
      formData.append("profile_image", "");
      const res = await updateUserImage({ data: formData, token }).unwrap();
      dispatch(setUserInfo({ ...res }));
    } catch (err) {
      CommonToast.error("Couldn't remove your photo. Please try again.");
    } finally {
      setImageBusy(false);
    }
  };

  return {
    userInfo,
    isEditMode,
    values,
    setField,
    error,
    saving,
    imageBusy,
    startEdit,
    cancelEdit,
    save,
    uploadImage,
    removeImage,
  };
};

export default useUserProfile;
